import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useAuth } from "../AuthContext";
import { Plus, Trash2, Upload, Pencil } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";
const emptySku = { sku_code: "", name: "", hsn: "", gst_rate: 18, unit: "SET", price: 0, upc: "" };
const emptyMap = { platform: "amazon", platform_sku: "", master_sku: "" };

export default function Skus() {
  const { user } = useAuth();
  const canEdit = user.role !== "checker";
  const [skus, setSkus] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [skuForm, setSkuForm] = useState(emptySku);
  const [skuEditId, setSkuEditId] = useState(null);
  const [skuOpen, setSkuOpen] = useState(false);
  const [mapForm, setMapForm] = useState(emptyMap);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapSearch, setMapSearch] = useState("");
  const [skuSearch, setSkuSearch] = useState("");
  const skuFileRef = useRef(null);
  const mapFileRef = useRef(null);

  const load = () => {
    api.get("/skus").then((r) => setSkus(r.data));
    api.get("/sku-mappings").then((r) => setMappings(r.data));
  };
  useEffect(() => { load(); }, []);

  const saveSku = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...skuForm, gst_rate: Number(skuForm.gst_rate), price: Number(skuForm.price) };
      if (skuEditId) await api.put(`/skus/${skuEditId}`, payload);
      else await api.post("/skus", payload);
      toast.success("SKU saved");
      setSkuOpen(false); setSkuForm(emptySku); setSkuEditId(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveMap = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sku-mappings", mapForm);
      toast.success("Mapping saved");
      setMapOpen(false); setMapForm(emptyMap); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const bulkUpload = async (file, endpoint) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd);
      const done = data.created !== undefined ? `${data.created} created, ${data.updated} updated` : `${data.processed} mappings${data.masters_created ? `, ${data.masters_created} new Master SKUs` : ""}`;
      toast.success(`Bulk upload complete: ${done}`, { description: data.errors?.length ? `${data.errors.length} errors: ${data.errors.slice(0, 3).join("; ")}` : undefined, duration: 8000 });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="skus-page" className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Masters</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>SKU Management</h1>
      </div>

      <Tabs defaultValue="master">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger data-testid="tab-master-skus" value="master">Master SKUs ({skus.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-sku-mappings" value="mappings">SKU Mappings ({mappings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="master" className="space-y-4 mt-4">
          {canEdit && (
            <div className="flex flex-wrap gap-3">
              <Dialog open={skuOpen} onOpenChange={(v) => { setSkuOpen(v); if (!v) { setSkuForm(emptySku); setSkuEditId(null); } }}>
                <DialogTrigger asChild>
                  <button data-testid="add-sku-btn" className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
                    <Plus size={15} /> Add SKU
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader><DialogTitle>{skuEditId ? "Edit SKU" : "Add Master SKU"}</DialogTitle></DialogHeader>
                  <form onSubmit={saveSku} className="grid sm:grid-cols-2 gap-4">
                    {[["sku_code", "SKU Code *", true], ["name", "Item Name *", true], ["hsn", "HSN *", true],
                      ["gst_rate", "GST Rate % *", true], ["unit", "Unit", false], ["price", "Default Rate", false], ["upc", "UPC / EAN", false]].map(([k, label, req]) => (
                      <div key={k} className={k === "name" ? "sm:col-span-2" : ""}>
                        <label className={labelCls}>{label}</label>
                        <input data-testid={`sku-${k}-input`} required={req} value={skuForm[k]}
                          type={["gst_rate", "price"].includes(k) ? "number" : "text"} step="0.01"
                          onChange={(e) => setSkuForm({ ...skuForm, [k]: e.target.value })} className={`${inputCls} mt-1`} />
                      </div>
                    ))}
                    <div className="sm:col-span-2 flex justify-end">
                      <button data-testid="sku-save-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">Save</button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              <input ref={skuFileRef} type="file" accept=".csv" className="hidden" onChange={(e) => { bulkUpload(e.target.files[0], "/skus/bulk"); e.target.value = ""; }} />
              <button data-testid="bulk-sku-btn" onClick={() => skuFileRef.current?.click()}
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-500 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
                <Upload size={15} /> Bulk CSV
              </button>
              <span className="text-xs text-slate-400 self-center">CSV columns: sku_code, name, hsn, gst_rate, unit, price, upc</span>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <div className="p-3 border-b border-slate-100">
              <input data-testid="sku-search-input" placeholder="Search SKUs..." value={skuSearch} onChange={(e) => setSkuSearch(e.target.value)}
                className="w-full sm:w-72 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20" />
            </div>
            <table className="w-full text-sm min-w-[700px]">
              <thead><tr className="bg-[#0A192F] text-white text-left">
                {["SKU Code", "Name", "HSN", "GST %", "Unit", "Rate", "UPC", ""].map((h) => <th key={h} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">{h}</th>)}
              </tr></thead>
              <tbody data-testid="skus-table-body" className="divide-y divide-slate-100">
                {skus.filter((s) => !skuSearch || s.sku_code.toLowerCase().includes(skuSearch.toLowerCase()) || s.name.toLowerCase().includes(skuSearch.toLowerCase())).slice(0, 300).map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors duration-200">
                    <td className="px-4 py-2.5 font-mono font-semibold">{s.sku_code}</td>
                    <td className="px-4 py-2.5">{s.name}</td>
                    <td className="px-4 py-2.5 font-mono">{s.hsn}</td>
                    <td className="px-4 py-2.5 text-right">{s.gst_rate}%</td>
                    <td className="px-4 py-2.5">{s.unit}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{Number(s.price).toFixed(2)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{s.upc || "—"}</td>
                    <td className="px-4 py-2.5">
                      {canEdit && (
                        <div className="flex gap-2 justify-end">
                          <button data-testid={`sku-edit-${s.sku_code}`} onClick={() => { setSkuForm({ ...emptySku, ...s }); setSkuEditId(s.id); setSkuOpen(true); }} className="text-slate-400 hover:text-[#0047AB]"><Pencil size={14} /></button>
                          <button data-testid={`sku-delete-${s.sku_code}`} onClick={async () => { await api.delete(`/skus/${s.id}`); load(); }} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {skus.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-slate-400">No SKUs yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="mappings" className="space-y-4 mt-4">
          {canEdit && (
            <div className="flex flex-wrap gap-3">
              <Dialog open={mapOpen} onOpenChange={setMapOpen}>
                <DialogTrigger asChild>
                  <button data-testid="add-mapping-btn" className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
                    <Plus size={15} /> Add Mapping
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader><DialogTitle>Add SKU Mapping</DialogTitle></DialogHeader>
                  <form onSubmit={saveMap} className="space-y-4">
                    <div>
                      <label className={labelCls}>Platform</label>
                      <select data-testid="mapping-platform-select" value={mapForm.platform} onChange={(e) => setMapForm({ ...mapForm, platform: e.target.value })} className={`${inputCls} mt-1`}>
                        <option value="amazon">Amazon</option><option value="flipkart">Flipkart</option>
                        <option value="blinkit">Blinkit</option><option value="firstcry">Firstcry</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Platform SKU *</label>
                      <input data-testid="mapping-platform-sku-input" required value={mapForm.platform_sku} onChange={(e) => setMapForm({ ...mapForm, platform_sku: e.target.value })} className={`${inputCls} mt-1 font-mono`} />
                    </div>
                    <div>
                      <label className={labelCls}>Master SKU *</label>
                      <input data-testid="mapping-master-sku-input" required list="master-sku-list" value={mapForm.master_sku} onChange={(e) => setMapForm({ ...mapForm, master_sku: e.target.value })} className={`${inputCls} mt-1 font-mono`} />
                      <datalist id="master-sku-list">{skus.map((s) => <option key={s.id} value={s.sku_code}>{s.name}</option>)}</datalist>
                    </div>
                    <div className="flex justify-end">
                      <button data-testid="mapping-save-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">Save</button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              <input ref={mapFileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { bulkUpload(e.target.files[0], "/sku-mappings/bulk"); e.target.value = ""; }} />
              <button data-testid="bulk-mapping-btn" onClick={() => mapFileRef.current?.click()}
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-500 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
                <Upload size={15} /> Bulk CSV / Excel
              </button>
              <span className="text-xs text-slate-400 self-center">CSV: platform, platform_sku, master_sku · Excel: sheets named per platform with Platform SKU / Master SKU / Tax Rate / HSN columns. Missing Master SKUs are auto-created.</span>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <div className="p-3 border-b border-slate-100">
              <input data-testid="mapping-search-input" placeholder="Search platform SKU or master SKU..." value={mapSearch} onChange={(e) => setMapSearch(e.target.value)}
                className="w-full sm:w-72 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20" />
            </div>
            <table className="w-full text-sm min-w-[500px]">
              <thead><tr className="bg-[#0A192F] text-white text-left">
                {["Platform", "Platform SKU", "Master SKU", ""].map((h) => <th key={h} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">{h}</th>)}
              </tr></thead>
              <tbody data-testid="mappings-table-body" className="divide-y divide-slate-100">
                {mappings.filter((m) => !mapSearch || m.platform_sku.toLowerCase().includes(mapSearch.toLowerCase()) || m.master_sku.toLowerCase().includes(mapSearch.toLowerCase())).slice(0, 300).map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors duration-200">
                    <td className="px-4 py-2.5 capitalize">{m.platform}</td>
                    <td className="px-4 py-2.5 font-mono">{m.platform_sku}</td>
                    <td className="px-4 py-2.5 font-mono font-semibold">{m.master_sku}</td>
                    <td className="px-4 py-2.5 text-right">
                      {canEdit && <button data-testid={`mapping-delete-${m.id}`} onClick={async () => { await api.delete(`/sku-mappings/${m.id}`); load(); }} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>}
                    </td>
                  </tr>
                ))}
                {mappings.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-slate-400">No mappings yet. Upload bulk CSV to map platform SKUs to Master SKUs.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
